import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';
import MarketingLinks from '../components/MarketingLinks';
import Toast from '../components/Toast';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { API_BASE_URL } from '../config/api';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  delete: (url, config = {}) => axios.delete(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

const STATUS_OPTIONS = ['Pending', 'Exclude', 'Sent', 'Responded'];
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const hasHtmlContent = (html) => {
  if (!html || !String(html).trim()) return false;
  const text = String(html).replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  return text.length > 0;
};

const escapeCsv = (val) => {
  const s = String(val ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const formatNotesForCsv = (notes) => {
  if (!notes || !Array.isArray(notes) || notes.length === 0) return '';
  return notes
    .map((n) => {
      const date = n.date ? new Date(n.date).toLocaleString('en-US') : '';
      const note = (n.note || '').replace(/\n/g, ' ').trim();
      return `${date}: ${note}`;
    })
    .join(' ;; ');
};

function OutreachPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState(null);

  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', type: '', description: '', email: '', websiteUrl: '', category: '', status: 'Pending' });

  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesContact, setNotesContact] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [editingNoteDateId, setEditingNoteDateId] = useState(null);
  const [editingNoteDateValue, setEditingNoteDateValue] = useState('');

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailContact, setEmailContact] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const [showEmailLogModal, setShowEmailLogModal] = useState(false);
  const [emailLogContact, setEmailLogContact] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [emailLogLoading, setEmailLogLoading] = useState(false);

  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showDeleteContactConfirm, setShowDeleteContactConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [viewingEmail, setViewingEmail] = useState(null);
  const [showTemplateEditModal, setShowTemplateEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateEditForm, setTemplateEditForm] = useState({ name: '', subject: '', message: '' });

  useEffect(() => {
    document.title = 'Outreach - Design Store Admin';
    fetchContacts();
    fetchTemplates();
  }, []);

  useEffect(() => {
    let filtered = [...contacts];
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }
    if (categoryFilter) {
      filtered = filtered.filter(c => (c.category || '') === categoryFilter);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(c =>
        (c.name || '').toLowerCase().includes(term) ||
        (c.email || '').toLowerCase().includes(term) ||
        (c.type || '').toLowerCase().includes(term) ||
        (c.description || '').toLowerCase().includes(term)
      );
    }
    setFilteredContacts(filtered);
  }, [contacts, statusFilter, categoryFilter, searchTerm]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/outreach/contacts`);
      if (response.data.success) {
        setContacts(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(err.response?.data?.error || 'Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await authAxios.get(`${API_BASE_URL}/admin/outreach/templates`);
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const categories = [...new Set(contacts.map(c => c.category).filter(Boolean))].sort();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Exclude': return 'bg-gray-100 text-gray-800';
      case 'Sent': return 'bg-blue-100 text-blue-800';
      case 'Responded': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const canSendEmail = (contact) => contact.email && emailRegex.test(contact.email.trim());

  const handleAddContact = () => {
    setEditingContact(null);
    setContactForm({ name: '', type: '', description: '', email: '', websiteUrl: '', category: '', status: 'Pending' });
    setShowContactModal(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      name: contact.name || '',
      type: contact.type || '',
      description: contact.description || '',
      email: contact.email || '',
      websiteUrl: contact.websiteUrl || '',
      category: contact.category || '',
      status: contact.status || 'Pending'
    });
    setShowContactModal(true);
  };

  const handleSaveContact = async () => {
    if (!contactForm.name.trim()) {
      setToast({ message: 'Name is required', type: 'error' });
      return;
    }
    try {
      if (editingContact) {
        const response = await authAxios.put(`${API_BASE_URL}/admin/outreach/contacts/${editingContact.id}`, contactForm);
        if (response.data.success) {
          setContacts(contacts.map(c => c.id === editingContact.id ? response.data.data : c));
          setToast({ message: 'Contact updated successfully', type: 'success' });
          setShowContactModal(false);
        }
      } else {
        const response = await authAxios.post(`${API_BASE_URL}/admin/outreach/contacts`, contactForm);
        if (response.data.success) {
          setContacts([response.data.data, ...contacts]);
          setToast({ message: 'Contact added successfully', type: 'success' });
          setShowContactModal(false);
        }
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to save contact', type: 'error' });
    }
  };

  const handleDeleteContact = (contact) => {
    setContactToDelete(contact);
    setShowDeleteContactConfirm(true);
  };

  const handleDeleteContactConfirm = async () => {
    if (!contactToDelete) return;
    try {
      const response = await authAxios.delete(`${API_BASE_URL}/admin/outreach/contacts/${contactToDelete.id}`);
      if (response.data.success) {
        setContacts(contacts.filter(c => c.id !== contactToDelete.id));
        setToast({ message: 'Contact deleted successfully', type: 'success' });
        setShowDeleteContactConfirm(false);
        setContactToDelete(null);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to delete contact', type: 'error' });
    }
  };

  const handleOpenNotes = (contact) => {
    setNotesContact(contact);
    setNewNote('');
    setEditingNoteDateId(null);
    setEditingNoteDateValue('');
    setShowNotesModal(true);
  };

  const handleAddNote = async () => {
    if (!notesContact || !newNote.trim()) return;
    try {
      const response = await authAxios.post(`${API_BASE_URL}/admin/outreach/contacts/${notesContact.id}/notes`, { note: newNote });
      if (response.data.success) {
        setContacts(contacts.map(c => c.id === notesContact.id ? response.data.data : c));
        setNotesContact(response.data.data);
        setNewNote('');
        setToast({ message: 'Note added successfully', type: 'success' });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to add note', type: 'error' });
    }
  };

  const toDatetimeLocal = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handleStartEditNoteDate = (n) => {
    setEditingNoteDateId(n.id || n.date);
    setEditingNoteDateValue(toDatetimeLocal(n.date));
  };

  const handleSaveNoteDate = async () => {
    if (!notesContact || !editingNoteDateId || !editingNoteDateValue) {
      setEditingNoteDateId(null);
      setEditingNoteDateValue('');
      return;
    }
    try {
      const response = await authAxios.put(
        `${API_BASE_URL}/admin/outreach/contacts/${notesContact.id}/notes/${editingNoteDateId}`,
        { date: new Date(editingNoteDateValue).toISOString() }
      );
      if (response.data.success) {
        setContacts(contacts.map(c => c.id === notesContact.id ? response.data.data : c));
        setNotesContact(response.data.data);
        setToast({ message: 'Note date updated', type: 'success' });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update date', type: 'error' });
    }
    setEditingNoteDateId(null);
    setEditingNoteDateValue('');
  };

  const handleNoteDateBlur = () => {
    if (editingNoteDateId) handleSaveNoteDate();
  };

  const handleNoteDateKeyDown = (e) => {
    if (e.key === 'Enter') handleSaveNoteDate();
    if (e.key === 'Escape') {
      setEditingNoteDateId(null);
      setEditingNoteDateValue('');
    }
  };

  const handleOpenSendEmail = (contact) => {
    setEmailContact(contact);
    setEmailSubject('');
    setEmailMessage('');
    setSelectedTemplateId('');
    setShowEmailModal(true);
  };

  const handleSelectTemplate = (e) => {
    const id = e.target.value;
    setSelectedTemplateId(id);
    const template = templates.find(t => t.id === id);
    if (template) {
      setEmailSubject(template.subject || '');
      setEmailMessage(template.message || '');
    }
  };

  const handleSendEmail = async () => {
    if (!emailContact || !emailSubject.trim() || !emailMessage.trim()) {
      setToast({ message: 'Subject and message are required', type: 'error' });
      return;
    }
    setEmailSending(true);
    try {
      const response = await authAxios.post(`${API_BASE_URL}/admin/outreach/contacts/${emailContact.id}/send-email`, {
        subject: emailSubject.trim(),
        message: emailMessage.trim()
      });
      if (response.data.success) {
        setContacts(contacts.map(c => c.id === emailContact.id ? { ...c, status: response.data.data?.status || c.status } : c));
        setToast({ message: 'Email sent successfully', type: 'success' });
        setShowEmailModal(false);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to send email', type: 'error' });
    } finally {
      setEmailSending(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!emailSubject.trim()) {
      setToast({ message: 'Subject is required to save as template', type: 'error' });
      return;
    }
    const name = prompt('Template name:', emailSubject.substring(0, 50));
    if (!name || !name.trim()) return;
    try {
      const response = await authAxios.post(`${API_BASE_URL}/admin/outreach/templates`, {
        name: name.trim(),
        subject: emailSubject.trim(),
        message: emailMessage.trim()
      });
      if (response.data.success) {
        setTemplates([response.data.data, ...templates]);
        setToast({ message: 'Template saved successfully', type: 'success' });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to save template', type: 'error' });
    }
  };

  const handleOpenEmailLog = async (contact) => {
    setEmailLogContact(contact);
    setShowEmailLogModal(true);
    setEmailLogLoading(true);
    try {
      const response = await authAxios.get(`${API_BASE_URL}/admin/outreach/contacts/${contact.id}/emails`);
      if (response.data.success) {
        setEmailLog(response.data.data);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to load email log', type: 'error' });
    } finally {
      setEmailLogLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateEditForm({
      name: template.name || '',
      subject: template.subject || '',
      message: template.message || ''
    });
    setShowTemplateEditModal(true);
  };

  const handleSaveTemplateEdit = async () => {
    if (!editingTemplate || !templateEditForm.name.trim()) {
      setToast({ message: 'Template name is required', type: 'error' });
      return;
    }
    try {
      const response = await authAxios.put(`${API_BASE_URL}/admin/outreach/templates/${editingTemplate.id}`, templateEditForm);
      if (response.data.success) {
        setTemplates(templates.map(t => t.id === editingTemplate.id ? response.data.data : t));
        setToast({ message: 'Template updated', type: 'success' });
        setShowTemplateEditModal(false);
        setEditingTemplate(null);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update template', type: 'error' });
    }
  };

  const handleStatusChange = async (contact, newStatus) => {
    try {
      const response = await authAxios.put(`${API_BASE_URL}/admin/outreach/contacts/${contact.id}`, { status: newStatus });
      if (response.data.success) {
        setContacts(contacts.map(c => c.id === contact.id ? response.data.data : c));
        setToast({ message: 'Status updated', type: 'success' });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update status', type: 'error' });
    }
  };

  const handleExportCsv = () => {
    const headers = ['Name', 'Type', 'Description', 'Email', 'Website URL', 'Category', 'Status', 'Notes'];
    const rows = filteredContacts.map((c) => [
      escapeCsv(c.name),
      escapeCsv(c.type),
      escapeCsv(c.description),
      escapeCsv(c.email),
      escapeCsv(c.websiteUrl),
      escapeCsv(c.category),
      escapeCsv(c.status),
      escapeCsv(formatNotesForCsv(c.notes))
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `outreach-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setToast({ message: `Exported ${filteredContacts.length} contacts`, type: 'success' });
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <AdminHeader />
          <AdminTabs />
          <MarketingLinks />
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading contacts...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <AdminHeader />
          <AdminTabs />
          <MarketingLinks />
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button onClick={fetchContacts} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <AdminHeader />
        <AdminTabs />
        <MarketingLinks />

        <div className="mb-6 flex flex-wrap items-end gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Outreach Contacts</h2>
            <p className="text-sm text-gray-600">Manage outreach contacts, send emails, and track responses.</p>
          </div>
          <div className="flex-1 flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="Search name, email, type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-48"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportCsv}
              disabled={filteredContacts.length === 0}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 text-sm"
            >
              Export CSV
            </button>
            <button
              onClick={() => setShowTemplatesModal(true)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
            >
              Templates
            </button>
            <button
              onClick={() => navigate('/admin', { state: { activeTab: 'outreach-email' } })}
              className="px-4 py-2 text-blue-600 hover:text-blue-800 text-sm"
            >
              Outreach Email Settings
            </button>
            <button
              onClick={handleAddContact}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              Add Contact
            </button>
          </div>
        </div>

        <div className="text-sm text-gray-600 mb-4">
          Showing {filteredContacts.length} of {contacts.length} contacts
        </div>

        {filteredContacts.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-600">No contacts found.</p>
            <button onClick={handleAddContact} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Add Contact</button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredContacts.map((contact) => (
                    <tr key={contact.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">
                        {contact.websiteUrl ? (
                          <a href={contact.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {contact.name}
                          </a>
                        ) : (
                          contact.name
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{contact.type}</td>
                      <td className="px-6 py-4 text-sm">
                        {contact.email ? (
                          <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">{contact.email}</a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={contact.status}
                          onChange={(e) => handleStatusChange(contact, e.target.value)}
                          className={`px-2 py-1 text-xs font-semibold rounded-full border-0 ${getStatusColor(contact.status)}`}
                        >
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-0">
                          <button onClick={() => handleEditContact(contact)} title="Edit" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => handleOpenNotes(contact)} title="Notes" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          </button>
                          {canSendEmail(contact) ? (
                            <button onClick={() => handleOpenSendEmail(contact)} title="Send Email" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            </button>
                          ) : (
                            <span title="No valid email" className="p-1 text-gray-300 cursor-not-allowed inline-flex">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            </span>
                          )}
                          <button onClick={() => handleOpenEmailLog(contact)} title="Email Log" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                          </button>
                          <button onClick={() => handleDeleteContact(contact)} title="Delete" className="p-1 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Contact Add/Edit Modal */}
        {showContactModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold">{editingContact ? 'Edit Contact' : 'Add Contact'}</h3>
                {editingContact && (
                  <div className="flex items-center gap-0">
                    <button onClick={() => handleOpenNotes(editingContact)} title="Notes" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    </button>
                    {canSendEmail(editingContact) ? (
                      <button onClick={() => handleOpenSendEmail(editingContact)} title="Send Email" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      </button>
                    ) : (
                      <span title="No valid email" className="p-1 text-gray-300 cursor-not-allowed inline-flex">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      </span>
                    )}
                    <button onClick={() => handleOpenEmailLog(editingContact)} title="Email Log" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm(f => ({ ...f, name: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="flex flex-wrap items-end gap-3 text-xs">
                  <div className="flex-1 min-w-[100px]">
                    <label className="block text-xs font-medium text-gray-600">Type</label>
                    <input
                      type="text"
                      value={contactForm.type}
                      onChange={(e) => setContactForm(f => ({ ...f, type: e.target.value }))}
                      placeholder="e.g. Coffee Shop"
                      className="mt-0.5 w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="flex-1 min-w-[100px]">
                    <label className="block text-xs font-medium text-gray-600">Category</label>
                    <input
                      type="text"
                      value={contactForm.category}
                      onChange={(e) => setContactForm(f => ({ ...f, category: e.target.value }))}
                      placeholder="e.g. Cave Creek Local"
                      className="mt-0.5 w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="flex-1 min-w-[80px]">
                    <label className="block text-xs font-medium text-gray-600">Status</label>
                    <select
                      value={contactForm.status}
                      onChange={(e) => setContactForm(f => ({ ...f, status: e.target.value }))}
                      className="mt-0.5 w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                    >
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={contactForm.description}
                    onChange={(e) => setContactForm(f => ({ ...f, description: e.target.value }))}
                    rows={2}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm(f => ({ ...f, email: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Website URL</label>
                  <input
                    type="url"
                    value={contactForm.websiteUrl}
                    onChange={(e) => setContactForm(f => ({ ...f, websiteUrl: e.target.value }))}
                    placeholder="https://"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setShowContactModal(false)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
                <button onClick={handleSaveContact} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save</button>
              </div>
            </div>
          </div>
        )}

        {/* Notes Modal */}
        {showNotesModal && notesContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-2">Notes for {notesContact.name}</h3>
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {(notesContact.notes || []).map((n) => (
                  <div key={n.id || n.date} className="p-2 bg-gray-50 rounded text-sm">
                    {editingNoteDateId === (n.id || n.date) ? (
                      <input
                        type="datetime-local"
                        value={editingNoteDateValue}
                        onChange={(e) => setEditingNoteDateValue(e.target.value)}
                        onBlur={handleNoteDateBlur}
                        onKeyDown={handleNoteDateKeyDown}
                        className="text-gray-500 text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoFocus
                      />
                    ) : (
                      <span
                        className="text-gray-500 cursor-pointer hover:text-gray-700 hover:underline"
                        onClick={() => handleStartEditNoteDate(n)}
                        title="Click to change date/time"
                      >
                        {formatDate(n.date)}
                      </span>
                    )}
                    <p className="mt-1">{n.note}</p>
                  </div>
                ))}
                {(!notesContact.notes || notesContact.notes.length === 0) && (
                  <p className="text-gray-500 text-sm">No notes yet.</p>
                )}
              </div>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setShowNotesModal(false)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Close</button>
                <button onClick={handleAddNote} disabled={!newNote.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Add Note</button>
              </div>
            </div>
          </div>
        )}

        {/* Send Email Modal */}
        {showEmailModal && emailContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-2">Send Email to {emailContact.name}</h3>
              <p className="text-sm text-gray-500 mb-4">To: {emailContact.email}</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                <select
                  value={selectedTemplateId}
                  onChange={handleSelectTemplate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">— Select template —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <div className="border border-gray-300 rounded-lg overflow-hidden [&_.ql-container]:min-h-[180px] [&_.ql-editor]:min-h-[180px]">
                  <ReactQuill
                    theme="snow"
                    value={emailMessage}
                    onChange={setEmailMessage}
                    modules={QUILL_MODULES}
                  />
                </div>
              </div>
              <div className="flex justify-between">
                <button onClick={handleSaveAsTemplate} className="px-4 py-2 text-blue-600 hover:underline">Save as template</button>
                <div className="flex gap-2">
                  <button onClick={() => setShowEmailModal(false)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
                  <button
                    onClick={handleSendEmail}
                    disabled={!emailSubject.trim() || !hasHtmlContent(emailMessage) || emailSending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {emailSending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Log Modal */}
        {showEmailLogModal && emailLogContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-2">Email Log for {emailLogContact.name}</h3>
              {emailLogLoading ? (
                <p className="text-gray-500">Loading...</p>
              ) : emailLog.length === 0 ? (
                <p className="text-gray-500">No emails sent yet.</p>
              ) : (
                <div className="space-y-2">
                  {emailLog.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => setViewingEmail(e)}
                      className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded text-sm transition-colors border border-transparent hover:border-gray-300"
                    >
                      <div className="text-gray-500">{formatDate(e.sentAt)}</div>
                      <div className="font-medium">{e.subject}</div>
                      <div className="text-gray-600 truncate">
                        {(() => {
                          const plain = (e.message || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                          return plain.length > 80 ? plain.substring(0, 80) + '...' : plain || '(no content)';
                        })()}
                      </div>
                      <span className="text-blue-600 text-xs mt-1 inline-block">Click to view</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <button onClick={() => { setShowEmailLogModal(false); setViewingEmail(null); }} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* View Email Modal */}
        {viewingEmail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-2">Email Sent {formatDate(viewingEmail.sentAt)}</h3>
              <div className="mb-4">
                <div className="text-sm text-gray-500 mb-1">To: {viewingEmail.contactEmail}</div>
                <div className="font-medium">{viewingEmail.subject}</div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg overflow-x-auto text-sm [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5">
                <div dangerouslySetInnerHTML={{ __html: viewingEmail.message || '' }} />
              </div>
              <div className="mt-4 flex justify-end">
                <button onClick={() => setViewingEmail(null)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Templates Modal */}
        {showTemplatesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Email Templates</h3>
              {templates.length === 0 ? (
                <p className="text-gray-500">No templates yet. Save one when composing an email.</p>
              ) : (
                <div className="space-y-2">
                  {templates.map((t) => (
                    <div key={t.id} className="p-3 bg-gray-50 rounded flex justify-between items-center">
                      <div>
                        <div className="font-medium">{t.name}</div>
                        <div className="text-sm text-gray-600 truncate">{t.subject}</div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditTemplate(t)}
                          className="text-blue-600 hover:underline text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Delete this template?')) return;
                            try {
                              await authAxios.delete(`${API_BASE_URL}/admin/outreach/templates/${t.id}`);
                              setTemplates(templates.filter(x => x.id !== t.id));
                              setToast({ message: 'Template deleted', type: 'success' });
                            } catch (err) {
                              setToast({ message: err.response?.data?.error || 'Failed to delete', type: 'error' });
                            }
                          }}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <button onClick={() => setShowTemplatesModal(false)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Template Edit Modal */}
        {showTemplateEditModal && editingTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Edit Template</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={templateEditForm.name}
                    onChange={(e) => setTemplateEditForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                  <input
                    type="text"
                    value={templateEditForm.subject}
                    onChange={(e) => setTemplateEditForm(f => ({ ...f, subject: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden [&_.ql-container]:min-h-[200px] [&_.ql-editor]:min-h-[200px]">
                    <ReactQuill
                      theme="snow"
                      value={templateEditForm.message}
                      onChange={(v) => setTemplateEditForm(f => ({ ...f, message: v }))}
                      modules={QUILL_MODULES}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => { setShowTemplateEditModal(false); setEditingTemplate(null); }} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
                <button onClick={handleSaveTemplateEdit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save</button>
              </div>
            </div>
          </div>
        )}

        <ConfirmationDialog
          isOpen={showDeleteContactConfirm}
          title="Delete Contact"
          message={`Are you sure you want to delete ${contactToDelete?.name}? This cannot be undone.`}
          onConfirm={handleDeleteContactConfirm}
          onCancel={() => { setShowDeleteContactConfirm(false); setContactToDelete(null); }}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />

        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </div>
  );
}

export default OutreachPage;
